import React, { useEffect, useState } from 'react';
import jm, { formatDateTime, photoUrl, referredBy } from '../api';
import CandidateTable, { NameCell, RowActions, StatusBadge } from '../common/CandidateTable';
import Modal from '../common/Modal';
import RatingRow from '../common/RatingRow';
import CandidateSummary, {
  DEMO_SCORE_FIELDS, PERSONAL_SCORE_FIELDS, PHONE_SCORE_FIELDS, ScoreTable, SectionHeader,
} from '../common/CandidateSummary';
import useStage from '../common/useStage';
import JoiningFields from '../common/JoiningFields';
import { EmployeeSelect, useMasters } from '../common/MastersContext';
import DateField, { MONTH_END, YEAR_START } from '../common/DateField';

const NEXT_STATUS = [
  { value: 'Hold', label: 'Hold (stay here)' },
  { value: 'Demo', label: 'Demo' },
  { value: 'Recruited', label: 'Recruitment' },
  { value: 'Joined', label: 'Joined (create employee)' },
  { value: 'Rejected', label: 'Rejected' },
];

const RATINGS = [
  { name: 'grooming', label: 'Grooming' },
  { name: 'body_language', label: 'Body Language' },
  { name: 'ability_learn', label: 'Ability to Learn' },
  { name: 'communication', label: 'Communication' },
  { name: 'team_player', label: 'Team Player' },
  { name: 'Responsible', label: 'Responsible' },
  { name: 'Dedicated', label: 'Dedicated' },
];

const Hold = ({ onMoved, onJoined }) => {
  const masters = useMasters();
  const [form, setForm] = useState({});

  const { rows, loading, editing, detail, detailLoading, openEdit, closeEdit, saving, save } =
    useStage(jm.hold, jm.saveHold, {
      onMoved: (status, result) => {
        if (status === 'Joined' && result.user_id) onJoined?.(result.user_id);
        onMoved?.(status, result);
      },
    });

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!detail) return;
    const stage = detail.hold || {};
    const recruit = detail.recruitment || {};

    setForm({
      recruitment_remark: stage.notes || '',
      recruit_by: recruit.recruit_by || '',
      recruitment_date: '',
      grooming: Number(recruit.grooming) || 0,
      body_language: Number(recruit.body_language) || 0,
      ability_learn: Number(recruit.ability_learn) || 0,
      communication: Number(recruit.communication) || 0,
      team_player: Number(recruit.team_player) || 0,
      Responsible: Number(recruit.Responsible) || 0,
      Dedicated: Number(recruit.Dedicated) || 0,
      job_status: 'Hold',
      demo_notes: '',
      reject_notes: '',
      emp_type: 1,
      prob_days: 90,
      employee_shift: '',
      contractor: '',
      contractor_type: '',
      joined_date: '',
      expected_joined_date: '',
      shift_code: '',
      visitor_type: '',
      incharge_id: '',
      hod_id: '',
      join_notes: '',
    });
  }, [detail]);

  const columns = [
    { key: 'action', label: 'Action', search: false, sortable: false, render: (row) => <RowActions row={row} onEdit={openEdit} /> },
    { key: 'job_title', label: 'Job Title' },
    { key: 'first_name', label: 'Candidate Name', render: (row) => <NameCell row={row} /> },
    { key: 'Referred_others', label: 'Referred By', render: referredBy, sortValue: referredBy },
    { key: 'email', label: 'Email' },
    { key: 'status', label: 'Status', search: false, sortable: false, render: () => <StatusBadge label="Hold" tone="warning" /> },
    { key: 'done_on', label: 'Held On', render: (row) => formatDateTime(row.done_on) },
  ];

  return (
    <>
      <CandidateTable title="Hold" rows={rows} columns={columns} loading={loading} exportName="hold_candidates" />

      <Modal
        show={!!editing}
        title={editing ? `${editing.first_name} ${editing.last_name || ''}` : ''}
        subtitle={editing ? editing.job_title : ''}
        photo={photoUrl(editing?.emp_img)}
        onClose={closeEdit}
        onSubmit={() => save(form)}
        saving={saving}
      >
        {detailLoading && <div className="text-center py-4">Loading…</div>}

        {!detailLoading && detail && (
          <>
            <CandidateSummary candidate={detail.application} />

            <ScoreTable title="Phone Interview" fields={PHONE_SCORE_FIELDS} row={detail.shortlisted} total={detail.scores.phone} />
            <ScoreTable title="Demo" fields={DEMO_SCORE_FIELDS} row={detail.demo} total={detail.scores.demo} />
            <ScoreTable title="Personal Interview 1" fields={PERSONAL_SCORE_FIELDS} row={detail.interview} total={detail.scores.personal} />
            <ScoreTable title="Final Interview" fields={PERSONAL_SCORE_FIELDS} row={detail.final} total={detail.scores.final} />

            <div className="form-group">
              <label>Hold Remark</label>
              <textarea className="form-control" rows="2" value={form.recruitment_remark || ''} onChange={(e) => set('recruitment_remark', e.target.value)} />
            </div>

            <div className="row">
              <div className="col-md-6 form-group required-tooltip">
                <label>Job Status</label>
                <select className="form-control required-input" value={form.job_status || ''} onChange={(e) => set('job_status', e.target.value)}>
                  {NEXT_STATUS.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {form.job_status === 'Recruited' && (
              <div className="border rounded p-3 mb-3">
                <SectionHeader title="Recruitment" />

                {RATINGS.map((field) => (
                  <RatingRow key={field.name} label={field.label} name={field.name} value={form[field.name]} onChange={set} />
                ))}

                <div className="row mt-2">
                  <div className="col-md-6 form-group">
                    <label>Recruit By</label>
                    <EmployeeSelect value={form.recruit_by} onChange={(v) => set('recruit_by', v)} employees={masters.employees} />
                  </div>
                  <div className="col-md-6 form-group">
                    <label>Recruitment Date</label>
                    <DateField
                    value={form.recruitment_date || ''}
                    onChange={(v) => set('recruitment_date', v)}
                    minDate={YEAR_START}
                    maxDate={MONTH_END}
                  />
                  </div>
                </div>
              </div>
            )}

            {form.job_status === 'Demo' && (
              <div className="form-group">
                <label>Demo Notes</label>
                <textarea className="form-control" rows="2" value={form.demo_notes || ''} onChange={(e) => set('demo_notes', e.target.value)} />
              </div>
            )}

            {form.job_status === 'Joined' && (
              <JoiningFields
                form={form}
                onChange={set}
                shifts={masters.shifts}
                hods={masters.hods}
                contractors={masters.contractors}
                contractTypes={masters.contractTypes}
              />
            )}

            {form.job_status === 'Rejected' && (
              <div className="form-group">
                <label>Reject Notes</label>
                <textarea className="form-control" rows="2" value={form.reject_notes || ''} onChange={(e) => set('reject_notes', e.target.value)} />
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default Hold;
